import { Database } from '@tamagui/lucide-icons';
import { useTranslation } from 'react-i18next';
import { XStack, YStack, Text, ScrollView, View, Separator, XStackProps } from 'tamagui';

import { InvalidConfiguration } from '../../../components/common/InvalidConfiguration';
import { LoadingOverlay } from '../../../components/common/LoadingOverlay';

interface HVACRawData {
  id: string;
  name: string;
  value: string | number;
  timestamp: number | null;
}

export interface HVACRawDataContentProps {
  data?: HVACRawData[] | null;
  isValidConfiguration: boolean;
  isLoading: boolean;
}

export const HVACRawDataContent = ({
  data,
  isValidConfiguration,
  isLoading,
}: HVACRawDataContentProps) => (
  <YStack flex={1} gap="$2">
    <LoadingOverlay isVisible={isLoading} />

    {!isLoading ? (
      !isValidConfiguration ? (
        <InvalidConfiguration />
      ) : !data || data.length === 0 ? (
        <HVACRawDataNoData />
      ) : (
        <YStack flex={1} gap="$3" marginBottom="$-5">
          <HVACRawDataHeaders hide={isLoading} />
          <ScrollView flex={1} contentContainerStyle={{ gap: '$3', paddingBottom: '$5' }}>
            {data.map((item) => (
              <HVACRawDataRow key={item.id} item={item} />
            ))}
          </ScrollView>
        </YStack>
      )
    ) : null}
  </YStack>
);

const HVACRawDataHeaders = ({ hide, ...rest }: XStackProps & { hide: boolean }) => {
  const { t } = useTranslation();

  if (hide) {
    return null;
  }

  return (
    <XStack alignItems="center" justifyContent="space-between" {...rest}>
      <View flexGrow={0.4} flexBasis={0} paddingHorizontal="$3">
        <Text fontSize="$5.5" color="$white12">
          {t('hvac.rawData.runningDevice')}
        </Text>
      </View>
      <View flexGrow={0.35} flexBasis={0} paddingHorizontal="$3">
        <Text fontSize="$5.5" color="$white12">
          {t('hvac.rawData.data')}
        </Text>
      </View>
      <View flexGrow={0.25} flexBasis={0} paddingHorizontal="$3" alignItems="center">
        <Text fontSize="$5.5" color="$white12" textAlign="center">
          {t('hvac.rawData.lastUpdated')}
        </Text>
      </View>
    </XStack>
  );
};

const HVACRawDataRow = ({ item }: { item: HVACRawData }) => {
  const { t } = useTranslation();

  const getTimestampStatus = (timestamp: number | null): 'normal' | 'warning' | 'critical' => {
    if (timestamp === null) {
      return 'critical';
    }

    const now = Date.now() / 1000;
    const ageInMinutes = (now - timestamp) / 60;

    if (ageInMinutes <= 5) {
      return 'normal';
    } else if (ageInMinutes <= 60) {
      return 'warning';
    } else {
      return 'critical';
    }
  };

  const getStatusColor = (status: 'normal' | 'warning' | 'critical') => {
    switch (status) {
      case 'normal':
        return '$green10';
      case 'warning':
        return '$yellow10';
      case 'critical':
        return '$red10';
      default:
        return '$hgPrimary';
    }
  };

  const formatTimestamp = (timestamp: number | null) => {
    if (!timestamp) {
      return t('hvac.rawData.noData');
    }

    return new Date(timestamp * 1000).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  return (
    <XStack
      alignItems="center"
      justifyContent="space-between"
      height={60}
      backgroundColor="$black12"
      borderWidth={1}
      borderColor="$transparentBorder"
      borderRadius="$3">
      <View flexGrow={0.4} flexBasis={0} paddingHorizontal="$3.5">
        <Text
          fontSize="$7"
          fontWeight="bold"
          color="$white12"
          numberOfLines={1}
          ellipsizeMode="tail">
          {item.name}
        </Text>
      </View>
      <Separator vertical height="100%" borderColor="$transparentBorder" />
      <View flexGrow={0.35} flexBasis={0} paddingHorizontal="$3">
        <Text fontSize="$7" color="$white12">
          {typeof item.value === 'number' ? `${item.value.toFixed(2)}` : item.value}
        </Text>
      </View>
      <Separator vertical height="100%" borderColor="$transparentBorder" />
      <View flexGrow={0.25} flexBasis={0} paddingHorizontal="$3" alignItems="center">
        <XStack
          alignItems="center"
          gap="$1"
          backgroundColor={getStatusColor(getTimestampStatus(item.timestamp))}
          paddingVertical="$1"
          paddingHorizontal="$1.5"
          borderRadius={100}>
          <Text fontSize="$5" fontWeight="bold" color="$white" padding="$1">
            {formatTimestamp(item.timestamp)}
          </Text>
        </XStack>
      </View>
    </XStack>
  );
};

const HVACRawDataNoData = () => {
  const { t } = useTranslation();

  return (
    <XStack
      flex={1}
      gap="$3.5"
      alignItems="center"
      justifyContent="center"
      backgroundColor="$black12"
      borderRadius="$3"
      marginTop="$-2.5">
      <Database size="$2.5" />
      <Text fontSize="$7.5" fontWeight="bold" color="$white12">
        {t('hvac.rawData.emptyMessage')}
      </Text>
    </XStack>
  );
};
